import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardBody, CardHeader, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PasswordInput } from '@/components/common/PasswordInput';
import { useChangePassword } from '@/features/profile/hooks';
import { changePasswordSchema, type ChangePasswordValues } from '@/features/profile/schema';
import { TwoStepCard } from './TwoStepCard';

/** Password and two-step verification for the current user (real identity endpoints). */
export function SecuritySection() {
  const change = useChangePassword();
  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = form.handleSubmit(async (v) => {
    await change.mutateAsync({ currentPassword: v.currentPassword, newPassword: v.newPassword });
    form.reset();
  });

  return (
    <div className="space-y-6">
    <Card className="max-w-2xl">
      <CardHeader title="Password" description="Choose a strong password you don't use elsewhere." />
      <CardBody>
        <form id="pw-form" onSubmit={onSubmit} className="space-y-4" noValidate>
          <PasswordInput {...form.register('currentPassword')} label="Current password" autoComplete="current-password"
            isInvalid={!!form.formState.errors.currentPassword} errorMessage={form.formState.errors.currentPassword?.message} />
          <PasswordInput {...form.register('newPassword')} label="New password" autoComplete="new-password"
            isInvalid={!!form.formState.errors.newPassword} errorMessage={form.formState.errors.newPassword?.message} />
          <PasswordInput {...form.register('confirmPassword')} label="Confirm new password" autoComplete="new-password"
            isInvalid={!!form.formState.errors.confirmPassword} errorMessage={form.formState.errors.confirmPassword?.message} />
        </form>
      </CardBody>
      <CardFooter>
        <Button color="primary" type="submit" form="pw-form" isLoading={change.isPending}>Update password</Button>
      </CardFooter>
    </Card>
    <TwoStepCard />
    </div>
  );
}
